import { cloneDeep, omit, sum } from 'lodash';
import { getFramework } from '@/framework';
import type { Border, Cell, CellStyle, TextAlignType } from '@/version-types';
import { getTableEditState } from '../plugins/edit/state';
import addOrUpdateLayer from '../utils/add-or-update-layer';
import { emptyBorder, isEqualBorder } from '../utils/cell';
import { getActiveTableLayer, getCellStyle } from '../utils/table';
import { applyTableLayout } from '../utils/table-layout';
import { cancelMergeCells, mergeCells } from './merge-cells';

export const getCellsActions = ({
    frameworkIdentity,
    mode,
}: {
    frameworkIdentity: string;
    mode: 'selectedCells' | 'allCells';
}) => {
    const framework = getFramework(frameworkIdentity);

    const getTargetCellIds = (mode: 'selectedCells' | 'allCells'): string[] => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return [];
        if (mode === 'allCells') {
            const cells = activeTableLayer.data.cells.flat().filter(Boolean) as Cell[];
            return cells.map((c) => c.__renderId);
        }
        const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getTableEditState(frameworkIdentity);
        if (cellsSelectedInfo) {
            return cellsSelectedInfo.cellInfoList.map((c) => c.id);
        }
        if (rowOrColSelectedRectInfo) {
            const startHIndex = rowOrColSelectedRectInfo.direction === 'vertical' ? rowOrColSelectedRectInfo.index : 0;
            const startVIndex =
                rowOrColSelectedRectInfo.direction === 'horizontal' ? rowOrColSelectedRectInfo.index : 0;
            const endHIndex =
                rowOrColSelectedRectInfo.direction === 'vertical'
                    ? rowOrColSelectedRectInfo.index + 1
                    : activeTableLayer.data.baseCells.horizontal.length;
            const endVIndex =
                rowOrColSelectedRectInfo.direction === 'horizontal'
                    ? rowOrColSelectedRectInfo.index + 1
                    : activeTableLayer.data.baseCells.vertical.length;
            const targetCells = activeTableLayer.data.cells
                .slice(startVIndex, endVIndex)
                .map((row) => row.slice(startHIndex, endHIndex))
                .flat()
                .filter(Boolean) as Cell[];
            return targetCells.map((c) => c.__renderId);
        }
        return [];
    };

    type CellStyleParams = Partial<Omit<CellStyle, 'border'>>;
    type CellStyleParamsKey = keyof CellStyleParams;

    const setCellStyle = async (styleParams: CellStyleParams) => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const targetCellIds = getTargetCellIds(mode);
        if (!targetCellIds.length) return;

        let newTableLayer = cloneDeep(activeTableLayer);
        const { cells: cellsProp, styles } = newTableLayer.data;
        const allCells = cellsProp.flat().filter(Boolean) as Cell[];
        const targetCells = allCells.filter((c) => targetCellIds.includes(c.__renderId));
        if (!targetCells.length) return;

        const setStyleItem = async <K extends CellStyleParamsKey>(type: K, value: CellStyleParams[K]) => {
            const newStyleId = (Math.max(...Object.keys(styles).map(Number)) + 1).toString();
            styles[newStyleId] = {
                [type]: value,
            };

            // 下划线和删除线互斥
            if (type === 'underline' && value) {
                styles[newStyleId].lineThrough = false;
            } else if (type === 'lineThrough' && value) {
                styles[newStyleId].underline = false;
            }

            targetCells.forEach((cell) => {
                // 去掉多余的引用, 防止数据越来越大
                cell.styleIds = cell.styleIds.filter((id) => !(type in newTableLayer.data.styles[id]));
                cell.styleIds.push(newStyleId);
            });
        };

        const paramsKeys = Object.keys(styleParams);
        const promises = paramsKeys.map((key) => setStyleItem(key as CellStyleParamsKey, styleParams[key]));
        await Promise.all(promises);

        const {
            hasEditingText,
            baseCellsBeforeEditing,
            activeCell: _activeCell,
        } = getTableEditState(frameworkIdentity);
        const activeCell = newTableLayer.data.cells.flat().find((c) => c?.__renderId === _activeCell?.id);

        const shouldUpdateTableLayoutStyleKeys: CellStyleParamsKey[] = ['fontFamily', 'fontSize', 'disableAutoWrap'];
        if (paramsKeys.some((key) => shouldUpdateTableLayoutStyleKeys.includes(key as CellStyleParamsKey))) {
            if (styleParams.fontFamily) {
                // 需要先更新一次数据 (带上最新fontFamily), 否则layer-loading的plugin无法知道是哪些单元格需要loading
                newTableLayer = addOrUpdateLayer({
                    frameworkIdentity,
                    layer: newTableLayer,
                    resetMaterialIds: true,
                });
                framework.service.frame.setActiveLayer(newTableLayer.id);
            }

            const shrinkSpan =
                hasEditingText && activeCell && baseCellsBeforeEditing
                    ? {
                          cellId: activeCell.__renderId,
                          minimumSpanWidth: sum(
                              baseCellsBeforeEditing.horizontal.slice(
                                  activeCell.hIndex,
                                  activeCell.hIndex + activeCell.colSpan,
                              ),
                          ),
                      }
                    : undefined;
            applyTableLayout(newTableLayer, { mode: 'repair', shrinkSpan });
        }

        newTableLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newTableLayer,
            resetMaterialIds: paramsKeys.includes('fontFamily'),
        });
        framework.service.frame.setActiveLayer(newTableLayer.id);
        framework.service.history.write(cloneDeep(activeTableLayer), newTableLayer);
    };

    type BizTextAlign = 'top' | 'vertical-center' | 'bottom' | 'left' | 'center' | 'right' | 'justify';
    interface BizCellStyleParams extends Omit<CellStyleParams, 'textAlign' | 'verticalAlign'> {
        align?: BizTextAlign;
    }
    type BizCellStyleParamsKey = keyof BizCellStyleParams;
    const setCellStyleByBiz = async (bizStyleParams: BizCellStyleParams) => {
        const styleParams: CellStyleParams = omit(bizStyleParams, ['align']);
        if (bizStyleParams.align) {
            if (['top', 'vertical-center', 'bottom'].includes(bizStyleParams.align)) {
                styleParams.verticalAlign = {
                    top: 'top',
                    'vertical-center': 'center',
                    bottom: 'bottom',
                }[bizStyleParams.align];
            } else {
                styleParams.textAlign = bizStyleParams.align as TextAlignType;
            }
        }
        return setCellStyle(styleParams);
    };

    const getSetCellStyleFn =
        <K extends BizCellStyleParamsKey>(type: K) =>
        (value: NonNullable<BizCellStyleParams[K]>) =>
            setCellStyleByBiz({ [type]: value });

    const setDisableAutoWrap = getSetCellStyleFn('disableAutoWrap');
    const setFontFamily = getSetCellStyleFn('fontFamily');
    const setFontSize = getSetCellStyleFn('fontSize');
    const setTextColor = getSetCellStyleFn('color');
    const setTextAttr = (mode: 'bold' | 'italic' | 'underline' | 'lineThrough', active: boolean) =>
        getSetCellStyleFn(mode)(active);
    const setBackgroundColor = getSetCellStyleFn('backgroundColor');
    const setTextAlign = getSetCellStyleFn('align');
    const setTextStyle = (textStyle: { fontFamily: string; fontSize: number; bold: boolean; italic: boolean }) =>
        setCellStyleByBiz(textStyle);

    const clearBorders = () => appendBorders('full', { ...emptyBorder });

    const appendBorders = (mode: 'wrap' | 'full' | 'top' | 'left' | 'right' | 'bottom', border: Border) => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;

        // 只支持表格整体的边框修改, 不支持某些单元格的边框修改
        const targetCellIds = getTargetCellIds('allCells');

        let newTableLayer = cloneDeep(activeTableLayer);

        type CellStyleBorderProp = NonNullable<CellStyle['border']>;
        const borderStylePool: { border: CellStyleBorderProp; styleId: string }[] = [];
        Object.entries(newTableLayer.data.styles).forEach(([styleId, style]) => {
            const styleKeys = Object.keys(style);
            if (styleKeys.length === 1 && styleKeys[0] === 'border') {
                borderStylePool.push({
                    styleId,
                    border: style.border!,
                });
            }
        });
        /** 返回styleId */
        function appendNewBorderStyle(border: CellStyleBorderProp): string {
            const isEqualBorderProp = (a: CellStyleBorderProp, b: CellStyleBorderProp) =>
                isEqualBorder(a.top, b.top) &&
                isEqualBorder(a.right, b.right) &&
                isEqualBorder(a.bottom, b.bottom) &&
                isEqualBorder(a.left, b.left);
            const poolItem = borderStylePool.find((item) => isEqualBorderProp(item.border, border));
            if (poolItem) return poolItem.styleId;

            const newStyleId = (Math.max(...Object.keys(styles).map(Number)) + 1).toString();
            styles[newStyleId] = { border };
            borderStylePool.push({ border, styleId: newStyleId });
            return newStyleId;
        }

        const { cells: cellsProp, styles } = newTableLayer.data;
        const cells = cellsProp.flat().filter(Boolean) as Cell[];
        const targetCells = cells.flat().filter((cell) => targetCellIds.includes(cell.__renderId));
        const sortedTargetCells = targetCells.sort((a, b) => (a.hIndex <= b.hIndex && a.vIndex <= b.vIndex ? -1 : 1));
        const lastCell = sortedTargetCells[sortedTargetCells.length - 1];
        const startHIndex = sortedTargetCells[0].hIndex;
        const startVIndex = sortedTargetCells[0].vIndex;
        const endHIndex = lastCell.hIndex + lastCell.colSpan;
        const endVIndex = lastCell.vIndex + lastCell.rowSpan;

        const appendOneSideBorder = (dir: 'top' | 'left' | 'bottom' | 'right') => {
            let sideCells: Cell[];
            switch (dir) {
                case 'top':
                    sideCells = targetCells.filter((c) => c && c.vIndex === startVIndex);
                    break;
                case 'left':
                    sideCells = targetCells.filter((c) => c && c.hIndex === startHIndex);
                    break;
                case 'bottom':
                    sideCells = targetCells.filter((c) => c && c.vIndex + c.rowSpan === endVIndex);
                    break;
                case 'right':
                    sideCells = targetCells.filter((c) => c && c.hIndex + c.colSpan === endHIndex);
                    break;
            }
            sideCells.forEach((cell) => {
                const style = getCellStyle(cell, newTableLayer.data);
                const newBorder: CellStyleBorderProp = {
                    top: { ...emptyBorder },
                    right: { ...emptyBorder },
                    bottom: { ...emptyBorder },
                    left: { ...emptyBorder },
                    ...style.border,
                    [dir]: border,
                };
                const newStyleId = appendNewBorderStyle(newBorder);
                // 去掉多余的引用, 防止数据越来越大
                cell.styleIds = cell.styleIds.filter((id) => !('border' in newTableLayer.data.styles[id]));
                cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
            });
        };

        switch (mode) {
            case 'wrap':
                appendOneSideBorder('top');
                appendOneSideBorder('left');
                appendOneSideBorder('bottom');
                appendOneSideBorder('right');
                break;
            case 'top':
            case 'left':
            case 'bottom':
            case 'right':
                appendOneSideBorder(mode);
                break;
            case 'full':
                {
                    const newStyleId = appendNewBorderStyle({
                        top: { ...border },
                        right: { ...border },
                        bottom: { ...border },
                        left: { ...border },
                    });
                    targetCells.forEach((cell) => {
                        // 去掉多余的引用, 防止数据越来越大
                        cell.styleIds = cell.styleIds.filter((id) => !('border' in newTableLayer.data.styles[id]));
                        if (cell.styleIds.includes(newStyleId)) {
                            cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
                        } else {
                            cell.styleIds.push(newStyleId);
                        }
                    });
                }
                break;
        }
        applyTableLayout(newTableLayer, { mode: 'repair' });
        newTableLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newTableLayer,
            resetMaterialIds: false,
        });
        framework.service.frame.setActiveLayer(newTableLayer.id);
        framework.service.history.write(cloneDeep(activeTableLayer), newTableLayer);
    };

    const mergeThisCells = () => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const targetCellIds = getTargetCellIds(mode);
        mergeCells({
            frameworkIdentity,
            tableLayer: activeTableLayer,
            targetCellIds,
        });
    };

    const cancelMergeThisCells = () => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const targetCellIds = getTargetCellIds(mode);
        if (targetCellIds.length !== 1) return;
        cancelMergeCells({
            frameworkIdentity,
            tableLayer: activeTableLayer,
            targetCellId: targetCellIds[0],
        });
    };

    return {
        setDisableAutoWrap,
        setFontFamily,
        setFontSize,
        setTextColor,
        setTextAlign,
        setTextAttr,
        setTextStyle,
        setBackgroundColor,
        clearBorders,
        appendBorders,
        mergeCells: mergeThisCells,
        cancelMergeCells: cancelMergeThisCells,
    };
};

/** 表格面板可调用的单元格样式、边框及合并操作集合。 */
export type CellsActions = ReturnType<typeof getCellsActions>;
